import _ from 'lodash';
import {
	SET_ACTIVE_TEAM_CONVERSATION,
	SET_TEAM_CONVERSATIONS,
	ADD_TEAM_CONVERSATION,
	UPDATE_TEAM_CONVERSATION,
	ADD_MESSAGE_TO_TEAM_CONVERSATION,
	CHANGE_CONVERSATION_TITLE,
	ADD_MESSAGES_TO_TEAM_CONVERSATION,
	REPLACE_MESSAGES_IN_TEAM_CONVERSATION,
	REMOVE_TEAM_CONVERSATION,
	SET_ACTIVE_TEAM_CONVERSATION_TO_NULL,
	APPEND_TEAM_CONVERSATIONS,
	UPDATE_TEAM_MESSAGE,
	SET_TEAM_REPLY,
} from '../actions/types.js';

const INITIAL_STATE = {
	teamConversations: [],
	_activeTeamConversation: null,
	initialLoad: { businessId: null, status: 'idle' },
	// the message being replied to, by conversation _id
	replyTo: {},
};

// Folds a fresh fetch into the loaded conversations by _id. Fetched messages
// win so edits and reactions made while offline show up, and older pages
// that were already loaded are kept.
const mergeTeamConversations = (loaded, fetched) => {
	const fetchedById = new Map(fetched.map((c) => [c._id, c]));

	const merged = loaded.map((conversation) => {
		const fresh = fetchedById.get(conversation._id);
		if (!fresh) {
			return conversation;
		}

		fetchedById.delete(conversation._id);
		return {
			...conversation,
			...fresh,
			teamMessages: _.uniqBy(
				[...(fresh.teamMessages || []), ...(conversation.teamMessages || [])],
				'_id'
			),
		};
	});

	return [...merged, ...fetchedById.values()];
};

export default function teamsReducer(state = INITIAL_STATE, action) {
	switch (action.type) {
		case 'CUSTOMER_PHOTO_SAVED':
			return {
				...state,
				teamConversations: state.teamConversations.map((conversation) =>
					conversation._customer?._id === action.payload._id
						? { ...conversation, _customer: { ...conversation._customer, photoUrl: action.payload.photoUrl } }
						: conversation),
			};
		case SET_TEAM_REPLY: {
			const { _teamConversation, message } = action.payload;
			const replyTo = { ...state.replyTo };

			if (message) {
				replyTo[_teamConversation] = message;
			} else {
				delete replyTo[_teamConversation];
			}

			return { ...state, replyTo };
		}

		case SET_ACTIVE_TEAM_CONVERSATION_TO_NULL:
			return {
				...state,
				_activeTeamConversation: null,
			};

		case 'team_conversations_load_state':
			return { ...state, initialLoad: action.payload };
		case SET_TEAM_CONVERSATIONS:
			return { ...state,
				teamConversations: action.meta?.merge
					? mergeTeamConversations(state.teamConversations, action.payload)
					: action.payload,
				initialLoad: action.meta?.businessId ? { businessId: action.meta.businessId, status: 'ready' } : state.initialLoad };


		case SET_ACTIVE_TEAM_CONVERSATION:
			return {
				...state,
				_activeTeamConversation: action.payload,
			}; // _id of conversation

		case ADD_TEAM_CONVERSATION: {
			if (!action.payload._id) {
				return state;
			}

			const existingConversation = state.teamConversations.find(
				(c) => c._id === action.payload._id
			);

			if (existingConversation) {
				const updatedConversations = state.teamConversations.map(
					(conversation) => {
						if (conversation._id === action.payload._id) {
							let uniqueMessages;

							if (action.payload.teamMessages) {
								// fetched messages win, so reopening a customer
								// notepad shows notes edited or deleted elsewhere
								uniqueMessages = _.uniqBy(
									[
										...action.payload.teamMessages,
										...conversation.teamMessages,
									],
									'_id'
								);

								const latestMessageDate = Math.max(
									new Date(
										conversation.latestMessageDate
									).getTime(),
									...action.payload.teamMessages.map(
										(message) =>
											new Date(
												message.createdAt
											).getTime()
									)
								);

								return {
									...conversation,
									...action.payload,
									teamMessages: uniqueMessages,
									latestMessageDate: new Date(
										latestMessageDate
									).toISOString(),
								};
							}
						}

						return conversation;
					}
				);
				return {
					...state,
					teamConversations: updatedConversations,
				};
			}

			return {
				...state,
				teamConversations: [...state.teamConversations, action.payload],
			};
		}

		case REPLACE_MESSAGES_IN_TEAM_CONVERSATION: {
			const { teamMessages, _teamConversation } = action.payload;

			const updatedConversations = state.teamConversations.map(
				(conversation) => {
					if (conversation._id === _teamConversation) {
						return {
							...conversation,
							teamMessages: [...teamMessages],
						};
					}
					return conversation;
				}
			);

			return {
				...state,
				teamConversations: updatedConversations,
			};
		}

		case UPDATE_TEAM_MESSAGE: {
			const { updatedTeamMessage } = action.payload;
			const { _teamConversation, _id } = updatedTeamMessage;

			const updatedTeamConversations = state.teamConversations.map(
				(conversation) => {
					if (conversation._id === _teamConversation) {
						const updatedTeamMessages =
							conversation.teamMessages.map((message) => {
								if (message._id === _id) {
									return {
										...message,
										...updatedTeamMessage,
									};
								}
								return message;
							});

						return {
							...conversation,
							teamMessages: updatedTeamMessages,
						};
					}
					return conversation;
				}
			);

			return {
				...state,
				teamConversations: updatedTeamConversations,
			};
		}

		case UPDATE_TEAM_CONVERSATION: {
			const updatedConversations = state.teamConversations.map(
				(conversation) => {
					if (conversation._id === action.payload._id) {
						return {
							...conversation,
							...action.payload,
						};
					}
					return conversation;
				}
			);

			return {
				...state,
				teamConversations: updatedConversations,
			};
		}

		case REMOVE_TEAM_CONVERSATION: {
			const updatedTeamConversations = state.teamConversations.filter(
				(conversation) =>
					conversation._id !== action.payload._teamConversation
			);

			if (
				state._activeTeamConversation ===
				action.payload._teamConversation
			) {
				return {
					...state,
					teamConversations: updatedTeamConversations,
					_activeTeamConversation: null,
				};
			}

			return {
				...state,
				teamConversations: updatedTeamConversations,
			};
		}

		case CHANGE_CONVERSATION_TITLE: {
			const updatedConversations = state.teamConversations.map(
				(conversation) => {
					if (conversation._id === action.payload._id) {
						return {
							...conversation,
							title: action.payload.title, // Update the title
						};
					}
					return conversation;
				}
			);

			return {
				...state,
				teamConversations: updatedConversations,
			};
		}

		case ADD_MESSAGES_TO_TEAM_CONVERSATION: {
			const { teamMessages, _teamConversation } = action.payload;

			const updatedConversations = state.teamConversations.map(
				(conversation) => {
					if (conversation._id === _teamConversation) {
						return {
							...conversation,
							teamMessages: _.uniqBy(
								[...conversation.teamMessages, ...teamMessages],
								'_id'
							),
						};
					}
					return conversation;
				}
			);

			return {
				...state,
				teamConversations: updatedConversations,
			};
		}

		case ADD_MESSAGE_TO_TEAM_CONVERSATION: {
			const { teamMessage, isSending, teamConversation } = action.payload;
			const _teamConversation = teamMessage?._teamConversation;

			if (!_teamConversation) {
				return state;
			}

			// A message for a conversation that is not loaded yet (new, or
			// older than the first page) brings the conversation in with it.
			if (!state.teamConversations.some((c) => c._id === _teamConversation)) {
				if (!teamMessage._id || teamConversation?._id !== _teamConversation) {
					return state;
				}

				return {
					...state,
					teamConversations: [
						...state.teamConversations,
						{
							...teamConversation,
							teamMessages: [{ ...teamMessage, isSending }],
							latestMessageDate: new Date().toISOString(),
						},
					],
				};
			}

			const updatedConversations = state.teamConversations.map(
				(conversation) => {
					if (conversation._id === _teamConversation) {
						const isDuplicateMessage =
							conversation.teamMessages.find(
								(m) => m._id === teamMessage._id
							);

						// The server copy has no attachments or messages, so it
						// is merged into the loaded one rather than replacing it.
						const updatedConversation = {
							...conversation,
							...teamConversation,
							teamMessages: conversation.teamMessages,
							latestMessageDate: new Date().toISOString(),
						};

						if (!isDuplicateMessage) {
							updatedConversation.teamMessages = [
								...conversation.teamMessages,
								{ ...teamMessage, isSending },
							];
						} else {
							updatedConversation.teamMessages =
								conversation.teamMessages.map((m) => {
									if (m._id === teamMessage._id) {
										return { ...m, isSending: false };
									}
									return m;
								});
						}

						return updatedConversation;
					}

					return conversation;
				}
			);

			return {
				...state,
				teamConversations: updatedConversations,
			};
		}

		case APPEND_TEAM_CONVERSATIONS: {
			const { previousTeamConversationsWithMessages } = action.payload;

			const newConversationsWithMessages = [
				...state.teamConversations,
				...previousTeamConversationsWithMessages,
			];

			const uniqueConversations = _.uniqBy(
				newConversationsWithMessages,
				'_id'
			);

			return {
				...state,
				teamConversations: uniqueConversations,
			};
		}

		default:
			return state;
	}
}
